/**
 * Researcher respondent address book ("Baza respondentów").
 *
 * Authenticated CRUD over /respondents/ and /respondent-tags/, plus the
 * per-study invite send (/projects/{id}/invite/). Distinct from the public,
 * unauthenticated `respondentService` used inside the respondent journey.
 */

import { API_CONFIG } from '@/config/api'
import { baseService } from './baseService'

export interface RespondentTag {
  id: number
  name: string
  respondent_count: number
  created_at?: string
}

export type ParticipationStatus = 'invited' | 'assigned' | 'in_progress' | 'completed' | 'cancelled'

/** The newest study this address-book entry was invited to. */
export interface LastStudy {
  project_id: number
  project_name: string
  status: ParticipationStatus
  /** Completed, else started, else invited — whichever happened last. */
  at: string
}

export interface AddressBookRespondent {
  id: number
  email: string
  name: string
  tags: RespondentTag[]
  created_at: string
  updated_at: string
  invited_count: number
  completed_count: number
  last_study: LastStudy | null
}

export interface InviteResult {
  sent: number
  skipped: number
  failed: string[]
}

/** DRF endpoints here disable pagination, but tolerate a `{results}` envelope. */
function unwrap<T>(data: T[] | { results: T[] }): T[] {
  return Array.isArray(data) ? data : (data?.results ?? [])
}

class RespondentBaseService {
  async listRespondents(): Promise<AddressBookRespondent[]> {
    const res = await baseService.get<AddressBookRespondent[] | { results: AddressBookRespondent[] }>(
      API_CONFIG.ENDPOINTS.RESPONDENTS.LIST,
    )
    return unwrap(res.data)
  }

  async createRespondent(data: { email: string; name?: string; tag_names?: string[] }): Promise<AddressBookRespondent> {
    const res = await baseService.post<AddressBookRespondent>(API_CONFIG.ENDPOINTS.RESPONDENTS.LIST, data)
    return res.data
  }

  async updateRespondent(
    id: number,
    data: { email?: string; name?: string; tag_names?: string[] },
  ): Promise<AddressBookRespondent> {
    const res = await baseService.patch<AddressBookRespondent>(API_CONFIG.ENDPOINTS.RESPONDENTS.DETAIL(id), data)
    return res.data
  }

  async deleteRespondent(id: number): Promise<void> {
    await baseService.delete(API_CONFIG.ENDPOINTS.RESPONDENTS.DETAIL(id))
  }

  async bulkAdd(data: { emails: string; tag_names?: string[] }): Promise<{
    created: number
    updated: number
    invalid: string[]
  }> {
    const res = await baseService.post<{ created: number; updated: number; invalid: string[] }>(
      API_CONFIG.ENDPOINTS.RESPONDENTS.BULK,
      data,
    )
    return res.data
  }

  async listTags(): Promise<RespondentTag[]> {
    const res = await baseService.get<RespondentTag[] | { results: RespondentTag[] }>(
      API_CONFIG.ENDPOINTS.RESPONDENTS.TAGS,
    )
    return unwrap(res.data)
  }

  async invite(
    projectId: number,
    selection: { respondent_ids?: number[]; tag_ids?: number[] },
  ): Promise<InviteResult> {
    const res = await baseService.post<InviteResult>(API_CONFIG.ENDPOINTS.PROJECTS.INVITE(projectId), {
      respondent_ids: selection.respondent_ids ?? [],
      tag_ids: selection.tag_ids ?? [],
    })
    return res.data
  }
}

export const respondentBaseService = new RespondentBaseService()
